import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const COMPANY_TYPES = [
  { id: "b2b_saas",          label: "B2B SaaS / Product",        desc: "Subscription software, product-led or sales-led growth" },
  { id: "ai_company",        label: "AI Company",                desc: "AI-native products, platforms, or services" },
  { id: "b2b_tech_services", label: "B2B Tech Services",         desc: "IT services, consulting, system integration, outsourcing" },
  { id: "b2b_manufacturing", label: "B2B Manufacturing",         desc: "Industrial, component, or process manufacturing" },
  { id: "b2b_professional",  label: "B2B Professional Services", desc: "Legal, accounting, advisory, staffing, agencies" },
  { id: "b2c_product",       label: "B2C Product",               desc: "Consumer goods, retail, FMCG" },
  { id: "b2c_digital",       label: "B2C Digital",               desc: "Consumer apps, digital media, subscriptions, fintech" },
  { id: "d2c",               label: "D2C",                       desc: "Direct-to-consumer brand, owned channel, ecommerce" },
];

const ROLES = [
  { id: "ceo",       label: "Founder / CEO",   desc: "Full revenue engine view — strategic gaps and capital efficiency" },
  { id: "cmo",       label: "CMO",             desc: "Brand, marketing effectiveness, pipeline contribution" },
  { id: "sales",     label: "Sales Head",      desc: "Pipeline, process, forecast accuracy, CRM health" },
  { id: "product",   label: "Product Head",    desc: "PLG motion, activation, expansion revenue" },
  { id: "category",  label: "Category Head",   desc: "Channel mix, pricing power, market positioning" },
];

const QUESTIONS_LOCAL = [
  { id: "b1", area: "brand",    text: "How clearly defined is your brand positioning and value proposition?",
    options: ["Not defined — ad-hoc messaging","Loosely defined — inconsistent","Partially documented — not consistently used","Well defined — mostly followed","Fully defined — consistently applied"] },
  { id: "b2", area: "brand",    text: "Do you have documented brand guidelines your team consistently follows?",
    options: ["No guidelines exist","Basic guidelines, rarely referenced","Guidelines exist, sometimes followed","Comprehensive guidelines followed by most","Full brand system enforced across all channels"] },
  { id: "b3", area: "brand",    text: "How consistently does your company publish thought leadership content?",
    options: ["Rarely or never","A few times a year","Monthly","Weekly","Multiple times per week, across channels"] },
  { id: "b4", area: "brand",    text: "How differentiated is your brand from your top 3 competitors?",
    options: ["We look and sound like everyone else","Slightly differentiated on a few dimensions","Differentiated in messaging but not delivery","Clearly differentiated — clients can articulate the difference","Category-defining — we shape how buyers think"] },
  { id: "b5", area: "brand",    text: "Do you have documented buyer persona messaging — pain points, value props, objection handling?",
    options: ["No persona documentation","Basic personas, no messaging maps","Personas + some messaging documented","Full persona messaging framework in use","Dynamic framework updated quarterly with data"] },
  { id: "m1", area: "marketing", text: "What percentage of your pipeline is sourced or influenced by marketing?",
    options: ["Less than 10%","10–25%","25–40%","40–60%","More than 60%"] },
  { id: "m2", area: "marketing", text: "Can you accurately attribute closed revenue to specific campaigns or channels?",
    options: ["No attribution tracking","Basic last-touch only","Multi-touch in some channels","Full multi-touch across all channels","Revenue attribution with predictive modelling"] },
  { id: "m3", area: "marketing", text: "Do you have a documented ICP with firmographic and behavioural criteria?",
    options: ["No ICP defined","Informal ICP — known but not documented","ICP documented but not consistently applied","ICP applied to targeting and scoring","ICP validated with data, updated quarterly"] },
  { id: "m4", area: "marketing", text: "How would you rate your marketing technology stack — tools, integrations, data flow?",
    options: ["Ad-hoc tools, no integration, manual reporting","Basic stack, some tools connected, lots of manual work","Reasonable stack, partially integrated","Well-integrated stack with automated reporting","Fully integrated, automated, real-time dashboards"] },
  { id: "m5", area: "marketing", text: "How aligned are your sales and marketing teams on lead definitions, SLAs, and handoff process?",
    options: ["No alignment — frequent blame between teams","Informal alignment — ad-hoc conversations","Some alignment — basic MQL/SQL definitions","Strong alignment — documented SLAs and regular syncs","Full smarketing model — shared targets, unified reporting"] },
  { id: "r1", area: "revops",   text: "How would you rate your CRM data quality, completeness, and team adoption?",
    options: ["CRM barely used — data incomplete and unreliable","Used by some, inconsistent data, no enforcement","Mostly adopted, reasonable quality with gaps","High adoption, good data quality, regular hygiene","CRM is the single source of truth — full enforcement"] },
  { id: "r2", area: "revops",   text: "Can you forecast next quarter's revenue with greater than 80% accuracy?",
    options: ["No formal forecasting process","Gut-feel forecasting, often wrong by 40%+","Basic pipeline-based forecast, 60–70% accurate","Structured forecast, usually 75–85% accurate","Data-driven forecast with >85% accuracy consistently"] },
  { id: "r3", area: "revops",   text: "Do you have a documented sales process with defined stage entry/exit criteria?",
    options: ["No documented sales process","Informal — each rep does it differently","Documented process, inconsistently followed","Documented with stage criteria, mostly followed","Rigorously enforced process with coaching cadence"] },
  { id: "r4", area: "revops",   text: "How healthy and predictable is your sales pipeline coverage and velocity?",
    options: ["Pipeline is lumpy, unpredictable, often empty","Inconsistent coverage — feast or famine","Reasonable coverage but velocity is unclear","3x+ coverage with visible velocity metrics","Optimised pipeline with stage-level conversion tracking"] },
  { id: "r5", area: "revops",   text: "Do you have a formal customer success or onboarding process to drive retention and expansion?",
    options: ["No formal CS — reactive support only","Basic onboarding, no structured CS motion","CS process exists but inconsistently executed","Structured CS with health scoring and QBRs","Proactive CS model driving measurable expansion revenue"] },
];

const AREA_COLORS = { brand: "#7C3AED", marketing: "#00B4A0", revops: "#F59E0B" };
const AREA_LABELS = { brand: "Brand", marketing: "Marketing", revops: "Revenue Operations" };

export default function AssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep]           = useState("company_type");
  const [companyType, setCompanyType] = useState("");
  const [role, setRole]           = useState("");
  const [meta, setMeta]           = useState({ company_name: "", respondent_name: "", respondent_email: "" });
  const [responses, setResponses] = useState({});
  const [qIndex, setQIndex]       = useState(0);
  const [error, setError]         = useState("");
  const [assessmentId, setAssessmentId] = useState(null); // eslint-disable-line no-unused-vars

  const currentQ = QUESTIONS_LOCAL[qIndex];
  const answered = Object.keys(responses).length;
  const total    = QUESTIONS_LOCAL.length;

  const handleAnswer = (idx) => {
    setResponses(prev => ({ ...prev, [currentQ.id]: idx }));
    if (qIndex < QUESTIONS_LOCAL.length - 1) {
      setQIndex(q => q + 1);
    } else {
      handleSubmit({ ...responses, [currentQ.id]: idx });
    }
  };

  const handleSubmit = async (finalResponses) => {
    setStep("submitting");
    try {
      const createRes = await axios.post(`${API}/api/assessment`, {
        company_type: companyType,
        company_name: meta.company_name,
        respondent_name: meta.respondent_name,
        respondent_email: meta.respondent_email,
        mode: "light",
      });
      const aid = createRes.data.assessment_id;
      setAssessmentId(aid);
      await axios.post(`${API}/api/assessment/${aid}/submit`, {
        responses: { ...finalResponses, _role: role },
      });
      navigate(`/results/${aid}`);
    } catch {
      setError("Something went wrong. Please try again.");
      setStep("questions");
    }
  };

  if (step === "company_type") {
    return (
      <Shell step={1} total={4} title="What type of company are you?" sub="Benchmarks and recommendations differ by business model.">
        <div className="grid sm:grid-cols-2 gap-3">
          {COMPANY_TYPES.map(ct => (
            <button
              key={ct.id}
              onClick={() => { setCompanyType(ct.id); setStep("role"); }}
              className="p-4 rounded-xl border border-gray-200 bg-white text-left hover:border-[#00B4A0] hover:shadow-sm transition-all group"
            >
              <p className="font-semibold text-sm text-gray-900 group-hover:text-[#00B4A0] transition-colors">{ct.label}</p>
              <p className="text-xs text-gray-400 mt-1">{ct.desc}</p>
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  if (step === "role") {
    return (
      <Shell step={2} total={4} title="What is your role?" sub="Your dashboard will highlight the areas most relevant to your perspective."
        onBack={() => setStep("company_type")}>
        <div className="grid sm:grid-cols-1 gap-3">
          {ROLES.map(r => (
            <button
              key={r.id}
              onClick={() => { setRole(r.id); setStep("meta"); }}
              className="p-4 rounded-xl border border-gray-200 bg-white text-left hover:border-[#7C3AED] hover:shadow-sm transition-all group flex items-center justify-between"
            >
              <div>
                <p className="font-semibold text-sm text-gray-900 group-hover:text-[#7C3AED] transition-colors">{r.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{r.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#7C3AED] flex-shrink-0 ml-3" />
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  if (step === "meta") {
    return (
      <Shell step={3} total={4} title="A few details" sub="So we can send you your results and personalise the report."
        onBack={() => setStep("role")}>
        <div className="space-y-4 max-w-md mx-auto">
          {[
            { key: "company_name",      label: "Company name", placeholder: "Acme Inc." },
            { key: "respondent_name",   label: "Your name",    placeholder: "Jane Smith" },
            { key: "respondent_email",  label: "Work email",   placeholder: "jane@acme.com" },
          ].map(({ key, label, placeholder }) => (
            <div key={key} className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
              <input
                type={key === "respondent_email" ? "email" : "text"}
                value={meta[key]}
                onChange={e => setMeta(m => ({ ...m, [key]: e.target.value }))}
                placeholder={placeholder}
                className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#00B4A0] transition-colors"
              />
            </div>
          ))}
          <p className="text-xs text-gray-400">All fields optional. Email lets us send you the results report.</p>
          <button
            onClick={() => setStep("questions")}
            className="w-full h-11 bg-[#00B4A0] hover:bg-[#009383] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            Start assessment <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Shell>
    );
  }

  if (step === "questions") {
    const color = AREA_COLORS[currentQ.area];
    const prevAnswered = responses[currentQ.id] !== undefined;

    return (
      <Shell step={4} total={4} title="" sub="" minimal>
        <div className="mb-8">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
            <span className="font-mono">{answered}/{total} answered</span>
            <span className="font-mono">{Math.round((answered / total) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-[#00B4A0] transition-all duration-300"
                 style={{ width: `${(answered / total) * 100}%` }} />
          </div>
          <div className="flex gap-2 mt-3">
            {["brand", "marketing", "revops"].map(area => {
              const areaQs  = QUESTIONS_LOCAL.filter(q => q.area === area);
              const areaAns = areaQs.filter(q => responses[q.id] !== undefined).length;
              return (
                <div key={area} className="flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-full border"
                     style={{ borderColor: `${AREA_COLORS[area]}30`, color: AREA_COLORS[area], background: `${AREA_COLORS[area]}08` }}>
                  <span>{AREA_LABELS[area]}</span>
                  <span>{areaAns}/{areaQs.length}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs font-mono px-2.5 py-1 rounded-full border"
                style={{ color, borderColor: `${color}30`, background: `${color}08` }}>
            {AREA_LABELS[currentQ.area]}
          </span>
          <span className="text-xs text-gray-400 font-mono">Q{qIndex + 1} of {total}</span>
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-8 leading-relaxed">{currentQ.text}</h2>

        <div className="space-y-3">
          {currentQ.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleAnswer(idx)}
              className={`w-full p-4 rounded-xl border text-left text-sm transition-all
                ${responses[currentQ.id] === idx
                  ? "border-[#00B4A0] bg-[#00B4A0]/5 text-gray-900"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm"}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5
                  ${responses[currentQ.id] === idx ? "border-[#00B4A0] bg-[#00B4A0]" : "border-gray-300"}`}>
                  {responses[currentQ.id] === idx && <CheckCircle2 className="w-3 h-3 text-white" />}
                </div>
                <span>{opt}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => qIndex > 0 ? setQIndex(q => q - 1) : setStep("meta")}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {prevAnswered && qIndex < total - 1 && (
            <button
              onClick={() => setQIndex(q => q + 1)}
              className="flex items-center gap-1.5 text-sm text-[#00B4A0] hover:text-[#009383] transition-colors"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm text-center">{error}</p>}
      </Shell>
    );
  }

  return (
    <Shell step={4} total={4} title="" sub="" minimal>
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="w-8 h-8 text-[#00B4A0] animate-spin" />
        <p className="text-gray-900 font-semibold">Analysing your responses…</p>
        <p className="text-gray-400 text-sm">Benchmarking against your company type and generating recommendations.</p>
      </div>
    </Shell>
  );
}

function Shell({ children, step, total, title, sub, minimal, onBack }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="px-6 py-4 border-b border-gray-100 bg-white flex items-center justify-between">
        <img src="/ryvr-logo.png" alt="RYVR" className="h-7 w-auto" />
        {!minimal && (
          <div className="flex gap-1">
            {Array.from({ length: total }).map((_, i) => (
              <div key={i} className={`h-1 w-8 rounded-full transition-colors ${i < step ? "bg-[#00B4A0]" : "bg-gray-100"}`} />
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-2xl">
          {!minimal && (
            <>
              {onBack && (
                <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors mb-6">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              {title && <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>}
              {sub && <p className="text-gray-500 text-sm mb-8">{sub}</p>}
            </>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
